// HQ (NASA inside the Hub shell): the pure helpers the shell, side menu, switcher and HQ route share.
// Server-only HQ code (who may open HQ, NASA access from Clerk, venn-data's URL) is in hq.server.ts.
import { ClientType } from '~/lib/client-type';

/** HQ: the NASA dashboard inside the Hub shell (routes/_app+/hq+). */
export function isHqPath(pathname: string) {
  return pathname === '/hq' || pathname.startsWith('/hq/');
}

/**
 * A NASA tab clicked off the dashboard (e.g. on /hq/organizations) only selects it in NASA's tab store,
 * which nothing on screen shows — so the rail also navigates back to the dashboard.
 */
export function hqTabNeedsNavigation(pathname: string): boolean {
  return pathname.replace(/\/+$/, '') !== '/hq';
}

/**
 * NASA's token is renewed once it has under this long left: the HQ route's loader renews it on the server, and the
 * page asks that loader once a token gets this close — early enough that no venn-data call carries an expired one.
 */
export const NASA_TOKEN_RENEW_BELOW_SECONDS = 60;

// --- Owner sessions: OWNERs see only NASA (/hq) ---

/**
 * An OWNER sees only the NASA app, so the shell bounces them to /hq from any other Hub route.
 * Guarded by `canOpenHq` (never bounce when HQ is gated off) and `isOnHqPath` (never loop on /hq).
 * A system admin in an owner session may also stay on the Organizations list, to switch to another tenant, and open an
 * owner's Brand Management from it.
 */
export function shouldRedirectOwnerToHq(params: {
  clientType: ClientType | undefined;
  canOpenHq: boolean;
  isOnHqPath: boolean;
  isSystemAdmin?: boolean;
  isOnOrganizationsIndex?: boolean;
  isOnOwnerBrandManagement?: boolean;
}): boolean {
  if (params.isSystemAdmin && (params.isOnOrganizationsIndex || params.isOnOwnerBrandManagement)) return false;
  return params.clientType === ClientType.OWNER && params.canOpenHq && !params.isOnHqPath;
}

/**
 * HQ needs its flag on, and then an owner session (owners live in HQ only), an admin — a Venn system admin or a
 * Clerk superadmin, either of whom may open any tenant's HQ without a Clerk membership — or NASA access for the
 * session's organization: the same rule that enables the HQ tile. Everyone else gets no token, so the token stays
 * behind the httpOnly cookie for them.
 */
export function mayOpenHq(hqEnabled: boolean, clientType: ClientType | undefined, nasaAccess: NasaAccess | null, isSystemAdmin = false): boolean {
  if (!hqEnabled) return false;
  if (clientType === ClientType.OWNER) return true;
  if (isSystemAdmin || nasaAccess?.isSuperadmin) return true;
  return Boolean(nasaAccess?.hasAccess);
}

/** The Organizations list itself, not an organization's settings under it. */
export function isOrganizationsIndexPath(pathname: string): boolean {
  return /^\/organizations\/?$/.test(pathname);
}

/** An owner's Brand Management, opened from the Organizations list, with its Upload logo modal. */
export function isOwnerBrandManagementPath(pathname: string): boolean {
  return /^\/organizations\/owners\/[^/]+\/brand-management(\/|$)/.test(pathname);
}

/**
 * The Platforms switcher shows only when there is a second platform to switch to — and never for an
 * OWNER, who is confined to NASA.
 */
export function shouldShowPlatforms(params: {
  clientType: ClientType | undefined;
  canOpenBackstage: boolean;
  canOpenHq: boolean;
  isBackstage: boolean;
  isHq: boolean;
}): boolean {
  if (params.clientType === ClientType.OWNER) return false;
  return params.canOpenBackstage || params.canOpenHq || params.isBackstage || params.isHq;
}

// --- Ask AI docked beside the dashboard ---
// How HQ's sidebar folds while NASA's Ask AI is docked, and opens again after: docking always folds the sidebar to
// make room; undocking opens it again only when docking was what folded it. Only the in-memory state moves, so the
// member's saved expanded/collapsed preference is untouched.
export interface AskAiSidebarState {
  sideMenuOpen: boolean;
  /** Docking Ask AI folded an open sidebar, so undocking should open it again. */
  foldedForAskAi: boolean;
}

export function sidebarForAskAi({ docked, sideMenuOpen, foldedForAskAi }: AskAiSidebarState & { docked: boolean }): AskAiSidebarState {
  if (docked) return { sideMenuOpen: false, foldedForAskAi: foldedForAskAi || sideMenuOpen };
  if (foldedForAskAi) return { sideMenuOpen: true, foldedForAskAi: false };
  return { sideMenuOpen, foldedForAskAi: false };
}

// --- NASA access from Clerk memberships ---
// A user may belong to several Clerk orgs; the matching one is the org whose publicMetadata entity id —
// venn_organization_id for PMC, venn_owner_id for OWNER — equals the id carried by the JWT. Any permission level
// grants access; an absent/invalid level grays the tile.

export interface ClerkMembershipLike {
  organization?: { public_metadata?: Record<string, unknown> | null } | null;
  public_metadata?: Record<string, unknown> | null;
}

export interface NasaAccess {
  hasAccess: boolean;
  permissionLevel: number | null;
  // A Clerk superadmin, whom NASA (venn-data) admits with any partner, membership or not.
  isSuperadmin?: boolean;
}

/** The superadmin flag as venn-data reads it: JSON true or the string "true" (any case, padded); nothing else. */
export function isClerkSuperadmin(publicMetadata: Record<string, unknown> | null | undefined): boolean {
  const value = publicMetadata?.superadmin;
  if (typeof value === 'string') return value.trim().toLowerCase() === 'true';
  return value === true;
}

/** permission_level is human-edited in Clerk, so it may arrive as a number or a numeric string. */
function toPermissionLevel(value: unknown): number | null {
  if (typeof value === 'number' && Number.isInteger(value)) return value;
  if (typeof value === 'string' && /^\d+$/.test(value.trim())) return Number.parseInt(value, 10);
  return null;
}

export function resolveNasaAccess(memberships: ClerkMembershipLike[], match: { key: string; id: string }): NasaAccess {
  const matched = memberships.find((membership) => membership.organization?.public_metadata?.[match.key] === match.id);
  if (!matched) return { hasAccess: false, permissionLevel: null };
  const permissionLevel = toPermissionLevel(matched.public_metadata?.permission_level);
  return { hasAccess: permissionLevel !== null, permissionLevel };
}
